import { BRASILIA_OFFSET, BRASILIA_OFFSET_MS } from './brasilia-date';

/**
 * Etiqueta "Novo" de item de menu (CU-17tm466mg60, pedido do Misael aprovado
 * em 09/10/2026 junto do protótipo do WhatsApp). Quais itens têm a etiqueta, e
 * até quando, é configuração global que o admin edita (`MenuNewBadgeService`).
 *
 * A etiqueta some por usuário quando ele abre o item pela primeira vez — a
 * marca fica no navegador (localStorage), como as preferências de
 * `list-preferences`, com o id do usuário na chave: num navegador compartilhado
 * (ou no admin que simula um cliente) a marca de um não esconde a do outro.
 * O logout limpa o localStorage inteiro, mas preserva estas marcas
 * (`keepNewBadgeMarks`). Some para todos depois da data de corte do item.
 * Navegador sem localStorage (modo privado restrito) mostra a etiqueta até a
 * data de corte, sem erro.
 *
 * A marca é da VERSÃO da etiqueta (`since`, o dia em que o admin a ligou): religar
 * um item, ou o "Mostrar de novo" da tela de admin, grava um `since` novo e a
 * etiqueta volta até para quem já tinha aberto. Estender a data mantém o `since`,
 * então quem já viu não volta a ver. Sem `since` (a lista semeada no deploy), a
 * chave é a da 1ª versão — as marcas que ela gravou continuam valendo.
 */
const PREFIX = 'sf.menu-new.';

/** Etiqueta de novidade: chave do item, último dia em que aparece e versão. */
export interface NewBadge {
  key: string;
  /** Último dia (AAAA-MM-DD, horário de Brasília) em que a etiqueta aparece. */
  until: string;
  /** Dia (AAAA-MM-DD) em que o admin ligou a etiqueta: a versão da marca de "já vi". */
  since?: string;
}

function storageKey(badge: NewBadge, userId: string): string {
  return badge.since
    ? `${PREFIX}${userId}.${badge.key}@${badge.since}`
    : `${PREFIX}${userId}.${badge.key}`;
}

/** A data de corte vale até o fim do dia em Brasília, em qualquer fuso de navegador. */
export function isNewBadgeExpired(badge: NewBadge, now: Date = new Date()): boolean {
  const end = new Date(`${badge.until}T23:59:59.999${BRASILIA_OFFSET}`);

  return isNaN(end.getTime()) || now.getTime() > end.getTime();
}

export function wasNewBadgeSeen(badge: NewBadge, userId: string): boolean {
  try {
    return localStorage.getItem(storageKey(badge, userId)) !== null;
  } catch {
    return false;
  }
}

export function markNewBadgeSeen(badge: NewBadge, userId: string): void {
  try {
    localStorage.setItem(storageKey(badge, userId), '1');
  } catch {
    // sem armazenamento: a etiqueta volta na próxima abertura, até a data de corte
  }
}

export function isNewBadgeVisible(badge: NewBadge | undefined, userId: string, now: Date = new Date()): boolean {
  return !!badge && !isNewBadgeExpired(badge, now) && !wasNewBadgeSeen(badge, userId);
}

/**
 * Roda `clear` (o `localStorage.clear()` do logout) sem perder as marcas de
 * "já vi": sem isto a etiqueta voltaria a cada login até a data de corte.
 */
export function keepNewBadgeMarks(clear: () => void): void {
  let marks: Array<[string, string]> = [];

  try {
    marks = Object.keys(localStorage)
      .filter(key => key.startsWith(PREFIX))
      .map(key => [key, localStorage.getItem(key)] as [string, string]);
  } catch {
    // sem armazenamento não há marca a guardar
  }

  clear();

  try {
    marks.forEach(([key, value]) => localStorage.setItem(key, value));
  } catch {
    // sem armazenamento: as marcas se perdem, a etiqueta volta até a data de corte
  }
}

/** Data de calendário real em AAAA-MM-DD (2026-02-30 não passa), como a API valida. */
export function isValidNewBadgeDay(day: string | null | undefined): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day || '');

  if (!match) {
    return false;
  }

  const date = new Date(Date.UTC(+match[1], +match[2] - 1, +match[3]));

  return date.getUTCFullYear() === +match[1] && date.getUTCMonth() === +match[2] - 1 && date.getUTCDate() === +match[3];
}

/** O dia (AAAA-MM-DD) em Brasília daqui a `days` dias, em qualquer fuso de navegador. */
export function brasiliaDayFrom(now: Date, days = 0): string {
  const brasiliaMs = now.getTime() + BRASILIA_OFFSET_MS + days * 24 * 60 * 60 * 1000;

  return new Date(brasiliaMs).toISOString().slice(0, 10);
}

/** Prazo sugerido ao ligar a etiqueta num item: ~30 dias, como a primeira leva. */
export const NEW_BADGE_DEFAULT_DAYS = 30;

/**
 * AAAA-MM-DD → DD/MM/AAAA só com texto: o `DatePipe` lê data sem hora como meia-noite
 * LOCAL, e formatar em outro fuso mostra o dia anterior num navegador a leste de UTC.
 */
export function formatNewBadgeDay(day: string | null | undefined): string {
  return isValidNewBadgeDay(day) ? day.split('-').reverse().join('/') : '';
}
